"use client";

// Error boundaries must be Client Components
import { ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/nextjs";
import Error from "next/error";
import { useEffect } from "react";

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & {
        digest?: string;
        message?: string;
        stack?: string;
    };
    reset: () => void;
}) {
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        // global-error must include html and body tags
        <html lang="en" className="h-full">
            <body className="h-full antialiased bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-gray-900 dark:via-blue-950/30 dark:to-indigo-950/20">
                <div className="min-h-full flex flex-col items-center justify-center p-4 md:p-8">
                    <div className="relative overflow-hidden w-full max-w-md rounded-3xl bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 dark:from-gray-800 dark:via-blue-950/30 dark:to-indigo-950/50 shadow-xl border border-white/20 dark:border-gray-700/30 backdrop-blur-sm">
                        {/* Decorative Elements */}
                        <div className="absolute top-0 right-0 -mt-8 -mr-8 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-3xl" />
                        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 h-32 w-32 rounded-full bg-gradient-to-tr from-purple-400/20 to-indigo-400/20 blur-3xl" />

                        {/* RTHM glassmorphic header */}
                        <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white overflow-hidden">
                            <div className="absolute inset-0 opacity-30">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-indigo-600/20"></div>
                            </div>
                            <div className="flex items-center relative z-10">
                                <div className="bg-white/30 p-2 rounded-xl mr-3 shadow-lg backdrop-blur-sm">
                                    <SparklesIcon className="h-5 w-5 text-white" />
                                </div>
                                <h1 className="text-2xl font-bold font-satoshi">
                                    RTHM
                                </h1>
                            </div>
                        </div>

                        <div className="relative p-6">
                            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 bg-clip-text text-transparent dark:from-white dark:via-blue-200 dark:to-indigo-200 mb-3 font-satoshi">
                                We encountered an issue
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4 font-suisse">
                                Our team has been notified and is working to
                                resolve this problem. Please try again or return
                                to the dashboard.
                            </p>

                            {/* Error details section */}
                            <details className="mb-6 bg-blue-50/50 dark:bg-blue-950/30 backdrop-blur-sm rounded-xl p-3 text-sm border border-gray-200/50 dark:border-gray-700/50">
                                <summary className="font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                                    Technical details
                                </summary>
                                <div className="mt-3 space-y-2">
                                    {error.message && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Error:
                                            </span>{" "}
                                            <span className="text-red-600 dark:text-red-400 font-mono">
                                                {error.message}
                                            </span>
                                        </div>
                                    )}
                                    {error.digest && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Digest:
                                            </span>{" "}
                                            <span className="font-mono text-gray-600 dark:text-gray-400">
                                                {error.digest}
                                            </span>
                                        </div>
                                    )}
                                    {error.stack && (
                                        <div>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                Stack:
                                            </span>
                                            <pre className="mt-1 p-2 bg-white/60 dark:bg-gray-900/40 backdrop-blur-sm rounded-lg overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50">
                                                {error.stack
                                                    .split("\n")
                                                    .slice(0, 5)
                                                    .join("\n")}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </details>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => reset()}
                                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
                                >
                                    <div className="flex flex-row items-center justify-center">
                                        <div className="leading-[14.30px] font-[599] font-satoshi tracking-wide text-base">
                                            Try again
                                        </div>
                                        <div className="flex items-center justify-center ml-2">
                                            <ArrowPathIcon className="h-5 w-5" />
                                        </div>
                                    </div>
                                </button>

                                <a
                                    href="/dashboard"
                                    className="py-2.5 min-h-12 px-4 rounded-full border flex justify-center items-center border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 backdrop-blur-sm transition-all duration-300 cursor-pointer"
                                >
                                    <div className="flex flex-row items-center justify-center">
                                        <div className="leading-[14.30px] font-[599] font-satoshi tracking-wide text-base">
                                            Return to dashboard
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </body>
        </html>
    );
}
